// Cliente del backend propio (Express, `/hub-api/*`). Es el ÚNICO canal: los 42 endpoints viven en
// `server/`, no hay cascada ni proxy intermedio.
//
// 🧹 15-sep-2026: el módulo se llamaba `n8n.js` y exportaba `n8nPost` / `n8nGet` / `n8nConfigured`
// — el nombre de una plataforma que se apagó el 28-ago y cuyos 45 workflows salieron del repo el
// 1-sep. Un nombre que describe algo que ya no existe manda a diagnosticar contra un sistema
// fantasma: alguien lee ese nombre y busca una plataforma que ya no está, no el `hub-api`. Ver `CLAUDE.md`, sesión
// 2026-09-15.

const TOKEN = process.env.REACT_APP_HUB_TOKEN || '';

// Default same-origin: el rewrite de Vercel (`vercel.json`) manda `/hub-api/*` a
// `hub-api-production-6ada.up.railway.app/api/*` — la URL de Railway, NO el dominio propio.
// 🪤 Este comentario decía `hub-api.solar-hub.co`, y ese dominio no resuelve desde el 6-sep:
// leerlo llevaba a diagnosticar el sitio contra un host muerto.
// Override: `REACT_APP_HUB_API_BASE` (vacío = sin backend).
const HUB_BASE = (process.env.REACT_APP_HUB_API_BASE ?? '/hub-api').replace(/\/+$/, '');
let hubVivo = HUB_BASE.length > 0;

/** `true` si el canal al backend propio sigue habilitado en esta sesión. */
export const hubApiActivo = () => hubVivo;
export const hubApiBaseUrl = () => HUB_BASE;
/** Solo para pruebas: rehabilita el canal tras una degradación. */
export const reiniciarHubApi = () => { hubVivo = HUB_BASE.length > 0; };

/** `true` si hay backend al que llamar. Lo usan ~20 servicios como gate. */
export const hubConfigurado = () => hubVivo;

function motivoError(data, status) {
  return (data && (data.error || data.message || data.motivo)) || `HTTP ${status}`;
}

function abortarPorTiempo(timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => {
    try { ctrl.abort(new DOMException(`Tiempo de espera agotado (${timeoutMs}ms).`, 'TimeoutError')); }
    catch { ctrl.abort(); }
  }, timeoutMs);
  return { ctrl, timer };
}

function releerAbort(e, ctrl, timeoutMs) {
  if (e?.name === 'AbortError' || /aborted/i.test(e?.message || '')) {
    const reason = ctrl.signal.reason;
    throw new Error(reason?.message || `Tiempo de espera agotado (${timeoutMs}ms).`);
  }
  throw e;
}

/** Un POST con timeout. Devuelve `{ status, data }`; lanza solo si la petición no llegó. */
async function postCrudo(url, body, timeoutMs) {
  const { ctrl, timer } = abortarPorTiempo(timeoutMs);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(TOKEN ? { 'x-alebas-token': TOKEN } : {}),
      },
      body: JSON.stringify(body || {}),
      signal: ctrl.signal,
    });
    const text = await res.text();
    let data;
    try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
    return { status: res.status, ok: res.ok, data, tipo: res.headers.get('content-type') || '' };
  } finally {
    clearTimeout(timer);
  }
}

function fallarCanal(path, detalle) {
  hubVivo = false;
  throw new Error(`Servicio (${path}) no disponible: ${detalle}`);
}

/** POST al backend propio. Único canal. */
async function pedirHub(path, body, timeoutMs) {
  if (!hubVivo) throw new Error('Servicio backend no configurado.');
  const url = `${HUB_BASE}/${String(path).replace(/^\/+/, '')}`;
  let r;
  try {
    r = await postCrudo(url, body, timeoutMs);
  } catch (e) {
    if (e?.name === 'AbortError' || /aborted|tiempo de espera/i.test(e?.message || '')) throw e;
    fallarCanal(path, e?.message || 'red');
  }

  if (r.data && typeof r.data.raw === 'string') {
    fallarCanal(path, 'el rewrite de /hub-api no está (llegó HTML del SPA)');
  }
  if (r.status === 502 || r.status === 503 || r.status === 504) {
    fallarCanal(path, motivoError(r.data, r.status));
  }
  if (!r.ok) {
    // El código y el cuerpo viajan con el error: sin ellos, quien llama no distingue un «no» del
    // negocio (409 del cupo, 422 de validación) de una caída, y termina tratándolos igual.
    const err = new Error(`Servicio (${path}) no disponible: ${motivoError(r.data, r.status)}`);
    err.status = r.status;
    err.data = r.data;
    throw err;
  }
  return r.data;
}

export async function hubGet(path, query = {}, { timeoutMs = 45000, headers = {} } = {}) {
  if (!hubVivo) throw new Error('Servicio backend no configurado.');
  const qs = new URLSearchParams(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const sufijo = `${String(path).replace(/^\/+/, '')}${qs ? `?${qs}` : ''}`;
  const { ctrl, timer } = abortarPorTiempo(timeoutMs);
  try {
    const res = await fetch(`${HUB_BASE}/${sufijo}`, {
      // 🪤 `headers` estaba DECLARADO en la firma y no se usaba: quien lo pasara se quedaba sin
      // sus cabeceras, sin error y sin nada que revisar — la misma familia del argumento que cae
      // en un parámetro inexistente y desaparece. Hoy hace falta de verdad: las rutas gateadas
      // por `exigeAdmin` leen el JWT del `Authorization`, y en un GET no hay cuerpo donde
      // mandarlo. Va DESPUÉS del token de la suite para que un llamador no pueda pisarlo.
      headers: { ...headers, ...(TOKEN ? { 'x-alebas-token': TOKEN } : {}) },
      signal: ctrl.signal,
    });
    const text = await res.text();
    let data;
    try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
    if (data && typeof data.raw === 'string') {
      fallarCanal(path, 'el rewrite de /hub-api no está (llegó HTML del SPA)');
    }
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      fallarCanal(path, motivoError(data, res.status));
    }
    if (!res.ok) {
      throw new Error(`Servicio (${path}) no disponible: ${motivoError(data, res.status)}`);
    }
    return data;
  } catch (e) {
    if (e?.message?.startsWith('Servicio') || e?.message?.includes('no configurado')) throw e;
    releerAbort(e, ctrl, timeoutMs);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Un ARCHIVO del backend (hoy, el PDF de una factura para el visor del admin). Devuelve un `Blob`.
 *
 * 🪤 A propósito NO llama a `fallarCanal`: que un documento no salga (Siigo lento, root
 * desplegándose) no puede apagar el canal de la sesión entera como si el hub-api se hubiera caído.
 * El error se lanza con el motivo del servidor y lo dice el visor.
 */
export async function hubGetBlob(path, { timeoutMs = 45000, headers = {} } = {}) {
  if (!hubVivo) throw new Error('Servicio backend no configurado.');
  const { ctrl, timer } = abortarPorTiempo(timeoutMs);
  try {
    const res = await fetch(`${HUB_BASE}/${String(path).replace(/^\/+/, '')}`, {
      headers: { ...headers, ...(TOKEN ? { 'x-alebas-token': TOKEN } : {}) },
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(`Servicio (${path}) no disponible: ${motivoError(data, res.status)}`);
    }
    return await res.blob();
  } catch (e) {
    if (e?.message?.startsWith('Servicio')) throw e;
    releerAbort(e, ctrl, timeoutMs);
  } finally {
    clearTimeout(timer);
  }
}

export async function hubPost(path, body, { timeoutMs = 45000 } = {}) {
  try {
    return await pedirHub(path, body, timeoutMs);
  } catch (e) {
    if (e?.name === 'AbortError' || /aborted/i.test(e?.message || '')) {
      throw new Error(e?.message || `Tiempo de espera agotado (${timeoutMs}ms).`);
    }
    throw e;
  }
}
